/**
 * i18n augmentation bundle for the v2 persona UI (PersonaCollectionDisplayV2,
 * PersonaAttributeEditorV2): label keys that were never added to the locale
 * translation.json files, plus display translations for the journeyStage,
 * informationSources.category and Schwartz-value enum vocabularies (stored
 * canonically in English slugs, translated at display time).
 *
 * Registered in app/i18n.js via addResourceBundle(deep-merge) — kept out of
 * the locale JSON files, which contain duplicate keys and must never be
 * parsed + re-serialized. Generated 2026-07-16; machine-translated, native
 * review welcome.
 */

const personaV2Labels = {
  "en": {
    "personas": {
      "collection": {
        "heuristicTooltip": "No academic anchor — this collection uses a heuristic baseline of common consumer-behaviour spokes.",
        "heuristic": "Heuristic baseline",
        "goals": "Goals",
        "painPoints": "Pain points",
        "journeyTitle": "Journey",
        "jtbdTitle": "The job",
        "objectionsTitle": "Objections",
        "voiceTitle": "Voice",
        "valuesAndSources": "Values & sources",
        "sections": "Sections",
        "drivenBy": "driven by",
        "feltThrough": "felt through",
        "journeyStage": "Journey stage",
        "antiPersona": "Not this persona",
        "triggers": "Triggers",
        "alternatives": "Current alternatives",
        "successCriteria": "Success:",
        "objectionsSub": "barriers to action",
        "rootedIn": "rooted in",
        "demonstrates": "demonstrates",
        "valuesTitle": "Values",
        "priority": "Priority",
        "sourcesTitle": "Information sources",
        "trust": "Trust"
      },
      "detailModal": {
        "showLess": "Show less",
        "showMore": "Show details"
      },
      "attributeEditor": {
        "frameworkBlurb": "Score how strongly this persona embodies each construct (1-10). Add a one-sentence interpretation.",
        "fallbackBlurb": "Heuristic baseline — score each dimension 1-10.",
        "importance": "Importance (1-10)",
        "linkConstruct": "Link to a framework construct",
        "noConstructLink": "— no link —",
        "regenerateDerivedBlurb": "Re-derive context, objections, values, sources, and voice from this persona\\'s construct profile.",
        "regenerating": "Generating…",
        "regenerate": "Regenerate",
        "journeySection": "Journey stage",
        "journeyStage": "Where they are in their journey",
        "journeyNone": "— select stage —",
        "journeyRationale": "Why this stage",
        "journeyRationalePlaceholder": "Tie this stage to specific construct scores...",
        "antiPersonaSection": "Not this persona",
        "antiPersonaBlurb": "What disqualifies this archetype. Mirror of a recruiting screener.",
        "antiPersonaPlaceholder": "Someone for whom this persona is wrong (not just different — wrong fit)...",
        "jtbdSection": "Job to be done",
        "jtbdJob": "The job they\\'re hiring this for",
        "jtbdJobPlaceholder": "e.g. Find a CRM that scales with my team without burying me in setup.",
        "jtbdSuccess": "Success criteria",
        "jtbdSuccessPlaceholder": "What ",
        "jtbdTriggers": "Triggers",
        "jtbdTriggersPlaceholder": "An event that moves them from passive to active...",
        "addTrigger": "Add trigger",
        "jtbdAlternatives": "Current alternatives",
        "jtbdAlternativesPlaceholder": "A status-quo solution, competitor, or workaround...",
        "addAlternative": "Add alternative",
        "objectionsSection": "Objections",
        "objectionsBlurb": "Barriers to action even when pain is felt. Each one points at the construct that drives it.",
        "noObjections": "No objections yet — click + Add objection, or regenerate from AI.",
        "objectionPlaceholder": "A barrier to action this persona feels...",
        "severity": "Severity (1-10)",
        "addObjection": "Add objection",
        "valuesSection": "Values",
        "noValues": "No values yet — click + Add value, or regenerate from AI.",
        "valueChoose": "— pick a value —",
        "valueRationalePlaceholder": "Why this matters to them...",
        "priority": "Priority (1-10)",
        "addValue": "Add value",
        "sourcesSection": "Information sources",
        "noSources": "No information sources yet — click + Add source, or regenerate from AI.",
        "sourcePlaceholder": "e.g. industry podcasts, LinkedIn thought leaders…",
        "sourceCategory": "Category",
        "sourceCategoryNone": "— category —",
        "trustLevel": "Trust level (1-10)",
        "addSource": "Add source",
        "quotesSection": "Voice samples",
        "quotesBlurb": "First-person utterances, each anchored to a construct or goal.",
        "noQuotes": "No voice samples yet — click + Add quote, or regenerate from AI.",
        "quotePlaceholder": "A first-person sentence in this persona\\'s voice...",
        "addQuote": "Add quote",
        "frameworkTab": "Framework",
        "contextTab": "Context & Voice",
        "regenerateBlurb": "Re-run the AI to suggest fresh goals & pain points for this persona.",
        "noFramework": "This collection has no framework defined.",
        "goalPlaceholder": "A specific goal this persona wants to achieve...",
        "addGoal": "Add goal",
        "noGoals": "No goals yet — click + Add goal, or regenerate from AI.",
        "painPlaceholder": "A specific frustration or unmet need...",
        "addPain": "Add pain point",
        "noPains": "No pain points yet — click + Add pain point, or regenerate from AI.",
        "derivingContext": "Deriving context, objections & voice…",
        "derivingContextBlurb": "The AI is computing this persona\\'s job-to-be-done, objections, values, information sources, and voice samples from their construct profile. This usually takes 10–20 seconds."
      },
      "enums": {
        "journeyStage": {
          "unaware": "Unaware",
          "considering": "Considering",
          "evaluating": "Evaluating",
          "deciding": "Deciding",
          "using": "Using",
          "advocating": "Advocating",
          "lapsing": "Lapsing"
        },
        "sourceCategory": {
          "digital-social": "Digital · Social",
          "digital-publishers": "Digital · Publishers",
          "podcasts-audio": "Podcasts · Audio",
          "peer-network": "Peer network",
          "manufacturer-direct": "Manufacturer direct",
          "industry-analysts": "Industry analysts",
          "traditional-print": "Traditional print",
          "in-person-events": "In-person events",
          "other": "Other"
        },
        "values": {
          "self-direction": "Self-direction",
          "achievement": "Achievement",
          "security": "Security",
          "benevolence": "Benevolence",
          "universalism": "Universalism",
          "hedonism": "Hedonism",
          "conformity": "Conformity",
          "tradition": "Tradition",
          "power": "Power",
          "stimulation": "Stimulation"
        }
      }
    }
  },
  "ja": {
    "personas": {
      "collection": {
        "heuristicTooltip": "学術的な基盤はありません。このコレクションは一般的な消費者行動の基準に基づいています。",
        "heuristic": "基準",
        "goals": "目標",
        "painPoints": "痛点",
        "journeyTitle": "旅",
        "jtbdTitle": "やるべき仕事",
        "objectionsTitle": "反対意見",
        "voiceTitle": "声",
        "valuesAndSources": "価値観と情報源",
        "sections": "セクション",
        "drivenBy": "駆動要因",
        "feltThrough": "感じられる",
        "journeyStage": "旅の段階",
        "antiPersona": "このペルソナではない",
        "triggers": "トリガー",
        "alternatives": "現在の代替案",
        "successCriteria": "成功：",
        "objectionsSub": "行動への障壁",
        "rootedIn": "根ざしている",
        "demonstrates": "示す",
        "valuesTitle": "価値観",
        "priority": "優先度",
        "sourcesTitle": "情報源",
        "trust": "信頼"
      },
      "detailModal": {
        "showLess": "少なく表示",
        "showMore": "詳細を表示"
      },
      "attributeEditor": {
        "frameworkBlurb": "このペルソナが各構成要素をどの程度体現しているかを評価します（1-10）。一文の解釈を追加してください。",
        "fallbackBlurb": "基準 — 各次元を1-10で評価します。",
        "importance": "重要度（1-10）",
        "linkConstruct": "フレームワーク構成要素へのリンク",
        "noConstructLink": "— リンクなし —",
        "regenerateDerivedBlurb": "このペルソナの構成プロファイルから文脈、反対意見、価値観、情報源、声を再生成します。",
        "regenerating": "生成中…",
        "regenerate": "再生成",
        "journeySection": "旅の段階",
        "journeyStage": "彼らの旅のどの段階にいるか",
        "journeyNone": "— 段階を選択 —",
        "journeyRationale": "この段階の理由",
        "journeyRationalePlaceholder": "この段階を特定の構成要素のスコアに結びつける...",
        "antiPersonaSection": "このペルソナではない",
        "antiPersonaBlurb": "このアーキタイプを不適格にする要因。リクルーティングスクリーナーの鏡。",
        "antiPersonaPlaceholder": "このペルソナが不適切な人（単に異なるだけでなく — 不適合）...",
        "jtbdSection": "やるべき仕事",
        "jtbdJob": "彼らがこれを雇う理由",
        "jtbdJobPlaceholder": "例：私のチームと共にスケールするCRMを見つけるが、設定に埋もれないように。",
        "jtbdSuccess": "成功基準",
        "jtbdSuccessPlaceholder": "何が",
        "jtbdTriggers": "トリガー",
        "jtbdTriggersPlaceholder": "彼らを受動的から能動的に動かすイベント...",
        "addTrigger": "トリガーを追加",
        "jtbdAlternatives": "現在の代替案",
        "jtbdAlternativesPlaceholder": "現状の解決策、競合、または回避策...",
        "addAlternative": "代替案を追加",
        "objectionsSection": "反対意見",
        "objectionsBlurb": "痛みを感じているときでも行動への障壁。各障壁はそれを駆動する構成要素を指し示します。",
        "noObjections": "まだ反対意見はありません — + 反対意見を追加をクリックするか、AIから再生成してください。",
        "objectionPlaceholder": "このペルソナが感じる行動への障壁...",
        "severity": "深刻度（1-10）",
        "addObjection": "反対意見を追加",
        "valuesSection": "価値観",
        "noValues": "まだ値がありません — + 値を追加をクリックするか、AIから再生成してください。",
        "valueChoose": "— 値を選択 —",
        "valueRationalePlaceholder": "なぜこれが彼らにとって重要なのか...",
        "priority": "優先度 (1-10)",
        "addValue": "値を追加",
        "sourcesSection": "情報源",
        "noSources": "まだ情報源がありません — + 情報源を追加をクリックするか、AIから再生成してください。",
        "sourcePlaceholder": "例: 業界ポッドキャスト、LinkedInの思想的リーダー…",
        "sourceCategory": "カテゴリ",
        "sourceCategoryNone": "— カテゴリ —",
        "trustLevel": "信頼レベル (1-10)",
        "addSource": "情報源を追加",
        "quotesSection": "声のサンプル",
        "quotesBlurb": "各構成要素または目標に基づく一人称の発言。",
        "noQuotes": "まだ声のサンプルがありません — + 引用を追加をクリックするか、AIから再生成してください。",
        "quotePlaceholder": "このペルソナの声での一人称の文...",
        "addQuote": "引用を追加",
        "frameworkTab": "フレームワーク",
        "contextTab": "コンテキストと声",
        "regenerateBlurb": "AIを再実行して、このペルソナの新しい目標と痛点を提案します。",
        "noFramework": "このコレクションには定義されたフレームワークがありません。",
        "goalPlaceholder": "このペルソナが達成したい具体的な目標...",
        "addGoal": "目標を追加",
        "noGoals": "まだ目標がありません — + 目標を追加をクリックするか、AIから再生成してください。",
        "painPlaceholder": "具体的なフラストレーションまたは未解決のニーズ...",
        "addPain": "痛点を追加",
        "noPains": "まだ痛点がありません — + 痛点を追加をクリックするか、AIから再生成してください。",
        "derivingContext": "コンテキスト、反論、声を導出中…",
        "derivingContextBlurb": "AIがこのペルソナの達成すべき仕事、反論、価値、情報源、声のサンプルをその構成プロファイルから計算しています。通常、これには10〜20秒かかります。"
      },
      "enums": {
        "journeyStage": {
          "unaware": "無知",
          "considering": "検討中",
          "evaluating": "評価中",
          "deciding": "決定中",
          "using": "使用中",
          "advocating": "支持中",
          "lapsing": "失効中"
        },
        "sourceCategory": {
          "digital-social": "デジタル · ソーシャル",
          "digital-publishers": "デジタル · 出版社",
          "podcasts-audio": "ポッドキャスト · オーディオ",
          "peer-network": "ピアネットワーク",
          "manufacturer-direct": "メーカー直販",
          "industry-analysts": "業界アナリスト",
          "traditional-print": "従来の印刷物",
          "in-person-events": "対面イベント",
          "other": "その他"
        },
        "values": {
          "self-direction": "自己指向",
          "achievement": "達成",
          "security": "安全",
          "benevolence": "慈善",
          "universalism": "普遍主義",
          "hedonism": "快楽主義",
          "conformity": "順応",
          "tradition": "伝統",
          "power": "権力",
          "stimulation": "刺激"
        }
      }
    }
  },
  "fr": {
    "personas": {
      "collection": {
        "heuristicTooltip": "Pas d'ancrage académique — cette collection utilise une base heuristique de porte-parole du comportement des consommateurs commun.",
        "heuristic": "Base heuristique",
        "goals": "Objectifs",
        "painPoints": "Points de douleur",
        "journeyTitle": "Parcours",
        "jtbdTitle": "Le travail",
        "objectionsTitle": "Objections",
        "voiceTitle": "Voix",
        "valuesAndSources": "Valeurs et sources",
        "sections": "Sections",
        "drivenBy": "motivé par",
        "feltThrough": "ressenti à travers",
        "journeyStage": "Étape du parcours",
        "antiPersona": "Pas ce persona",
        "triggers": "Déclencheurs",
        "alternatives": "Alternatives actuelles",
        "successCriteria": "Succès :",
        "objectionsSub": "barrières à l'action",
        "rootedIn": "ancré dans",
        "demonstrates": "démontre",
        "valuesTitle": "Valeurs",
        "priority": "Priorité",
        "sourcesTitle": "Sources d'information",
        "trust": "Confiance"
      },
      "detailModal": {
        "showLess": "Montrer moins",
        "showMore": "Montrer les détails"
      },
      "attributeEditor": {
        "frameworkBlurb": "Évaluez à quel point ce persona incarne chaque construct (1-10). Ajoutez une interprétation en une phrase.",
        "fallbackBlurb": "Base heuristique — évaluez chaque dimension de 1 à 10.",
        "importance": "Importance (1-10)",
        "linkConstruct": "Lier à un construct de cadre",
        "noConstructLink": "— pas de lien —",
        "regenerateDerivedBlurb": "Régénérer le contexte, les objections, les valeurs, les sources et la voix à partir du profil de construct de ce persona.",
        "regenerating": "Génération en cours…",
        "regenerate": "Régénérer",
        "journeySection": "Étape du parcours",
        "journeyStage": "Où ils en sont dans leur parcours",
        "journeyNone": "— sélectionner une étape —",
        "journeyRationale": "Pourquoi cette étape",
        "journeyRationalePlaceholder": "Lier cette étape à des scores de construct spécifiques...",
        "antiPersonaSection": "Pas ce persona",
        "antiPersonaBlurb": "Ce qui disqualifie cet archétype. Miroir d'un filtre de recrutement.",
        "antiPersonaPlaceholder": "Quelqu'un pour qui ce persona est inapproprié (pas juste différent — pas un bon fit)...",
        "jtbdSection": "Travail à réaliser",
        "jtbdJob": "Le travail pour lequel ils recrutent",
        "jtbdJobPlaceholder": "par exemple. Trouver un CRM qui évolue avec mon équipe sans m'encombrer de configuration.",
        "jtbdSuccess": "Critères de succès",
        "jtbdSuccessPlaceholder": "Quoi",
        "jtbdTriggers": "Déclencheurs",
        "jtbdTriggersPlaceholder": "Un événement qui les fait passer de passif à actif...",
        "addTrigger": "Ajouter un déclencheur",
        "jtbdAlternatives": "Alternatives actuelles",
        "jtbdAlternativesPlaceholder": "Une solution de statu quo, un concurrent ou un contournement...",
        "addAlternative": "Ajouter une alternative",
        "objectionsSection": "Objections",
        "objectionsBlurb": "Barrières à l'action même lorsque la douleur est ressentie. Chacune pointe vers le construct qui la motive.",
        "noObjections": "Pas encore d'objections — cliquez sur + Ajouter une objection, ou régénérez à partir de l'IA.",
        "objectionPlaceholder": "Une barrière à l'action que ce persona ressent...",
        "severity": "Gravité (1-10)",
        "addObjection": "Ajouter une objection",
        "valuesSection": "Valeurs",
        "noValues": "Aucune valeur pour le moment — cliquez sur + Ajouter une valeur, ou régénérez avec l'IA.",
        "valueChoose": "— choisissez une valeur —",
        "valueRationalePlaceholder": "Pourquoi cela compte pour eux...",
        "priority": "Priorité (1-10)",
        "addValue": "Ajouter une valeur",
        "sourcesSection": "Sources d'information",
        "noSources": "Aucune source d'information pour le moment — cliquez sur + Ajouter une source, ou régénérez avec l'IA.",
        "sourcePlaceholder": "par ex. podcasts de l'industrie, leaders d'opinion sur LinkedIn…",
        "sourceCategory": "Catégorie",
        "sourceCategoryNone": "— catégorie —",
        "trustLevel": "Niveau de confiance (1-10)",
        "addSource": "Ajouter une source",
        "quotesSection": "Exemples de voix",
        "quotesBlurb": "Énoncés à la première personne, chacun ancré à un construit ou un objectif.",
        "noQuotes": "Aucun exemple de voix pour le moment — cliquez sur + Ajouter une citation, ou régénérez avec l'IA.",
        "quotePlaceholder": "Une phrase à la première personne dans la voix de ce persona...",
        "addQuote": "Ajouter une citation",
        "frameworkTab": "Cadre",
        "contextTab": "Contexte & Voix",
        "regenerateBlurb": "Relancez l'IA pour suggérer de nouveaux objectifs et points de douleur pour ce persona.",
        "noFramework": "Cette collection n'a pas de cadre défini.",
        "goalPlaceholder": "Un objectif spécifique que ce persona souhaite atteindre...",
        "addGoal": "Ajouter un objectif",
        "noGoals": "Aucun objectif pour le moment — cliquez sur + Ajouter un objectif, ou régénérez avec l'IA.",
        "painPlaceholder": "Une frustration ou un besoin non satisfait spécifique...",
        "addPain": "Ajouter un point de douleur",
        "noPains": "Aucun point de douleur pour le moment — cliquez sur + Ajouter un point de douleur, ou régénérez avec l'IA.",
        "derivingContext": "Dérivation du contexte, des objections et de la voix…",
        "derivingContextBlurb": "L'IA calcule le travail à réaliser, les objections, les valeurs, les sources d'information et les exemples de voix de ce persona à partir de leur profil de construit. Cela prend généralement 10 à 20 secondes."
      },
      "enums": {
        "journeyStage": {
          "unaware": "Inconscient",
          "considering": "En considération",
          "evaluating": "En évaluation",
          "deciding": "En décision",
          "using": "En utilisation",
          "advocating": "En plaidoyer",
          "lapsing": "En déclin"
        },
        "sourceCategory": {
          "digital-social": "Numérique · Social",
          "digital-publishers": "Numérique · Éditeurs",
          "podcasts-audio": "Podcasts · Audio",
          "peer-network": "Réseau de pairs",
          "manufacturer-direct": "Fabricant direct",
          "industry-analysts": "Analystes de l'industrie",
          "traditional-print": "Impression traditionnelle",
          "in-person-events": "Événements en personne",
          "other": "Autre"
        },
        "values": {
          "self-direction": "Autonomie",
          "achievement": "Accomplissement",
          "security": "Sécurité",
          "benevolence": "Bienveillance",
          "universalism": "Universalisme",
          "hedonism": "Hédonisme",
          "conformity": "Conformité",
          "tradition": "Tradition",
          "power": "Pouvoir",
          "stimulation": "Stimulation"
        }
      }
    }
  },
  "de": {
    "personas": {
      "collection": {
        "heuristicTooltip": "Keine akademische Verankerung — diese Sammlung verwendet eine heuristische Basislinie gängiger Verbraucherverhaltensmuster.",
        "heuristic": "Heuristische Basislinie",
        "goals": "Ziele",
        "painPoints": "Schmerzpunkte",
        "journeyTitle": "Reise",
        "jtbdTitle": "Die Aufgabe",
        "objectionsTitle": "Einwände",
        "voiceTitle": "Stimme",
        "valuesAndSources": "Werte & Quellen",
        "sections": "Abschnitte",
        "drivenBy": "getrieben von",
        "feltThrough": "gefühlt durch",
        "journeyStage": "Reisestadium",
        "antiPersona": "Nicht diese Persona",
        "triggers": "Auslöser",
        "alternatives": "Aktuelle Alternativen",
        "successCriteria": "Erfolg:",
        "objectionsSub": "Hindernisse für Maßnahmen",
        "rootedIn": "verwurzelt in",
        "demonstrates": "zeigt",
        "valuesTitle": "Werte",
        "priority": "Priorität",
        "sourcesTitle": "Informationsquellen",
        "trust": "Vertrauen"
      },
      "detailModal": {
        "showLess": "Weniger anzeigen",
        "showMore": "Details anzeigen"
      },
      "attributeEditor": {
        "frameworkBlurb": "Bewerten Sie, wie stark diese Persona jedes Konstrukt verkörpert (1-10). Fügen Sie eine ein Satz lange Interpretation hinzu.",
        "fallbackBlurb": "Heuristische Basislinie — bewerten Sie jede Dimension 1-10.",
        "importance": "Bedeutung (1-10)",
        "linkConstruct": "Verknüpfen Sie mit einem Rahmenkonstrukt",
        "noConstructLink": "— keine Verknüpfung —",
        "regenerateDerivedBlurb": "Kontext, Einwände, Werte, Quellen und Stimme aus dem Konstruktprofil dieser Persona neu ableiten.",
        "regenerating": "Generiere…",
        "regenerate": "Neu generieren",
        "journeySection": "Reisestadium",
        "journeyStage": "Wo sie sich in ihrer Reise befinden",
        "journeyNone": "— Stadium auswählen —",
        "journeyRationale": "Warum dieses Stadium",
        "journeyRationalePlaceholder": "Verknüpfen Sie dieses Stadium mit spezifischen Konstruktbewertungen...",
        "antiPersonaSection": "Nicht diese Persona",
        "antiPersonaBlurb": "Was diesen Archetyp disqualifiziert. Spiegel eines Rekrutierungsscreeners.",
        "antiPersonaPlaceholder": "Jemand, für den diese Persona falsch ist (nicht nur anders — falsche Passform)...",
        "jtbdSection": "Aufgabe zu erledigen",
        "jtbdJob": "Die Aufgabe, für die sie dies einstellen",
        "jtbdJobPlaceholder": "z.B. Finde ein CRM, das mit meinem Team skaliert, ohne mich in der Einrichtung zu erdrücken.",
        "jtbdSuccess": "Erfolgskriterien",
        "jtbdSuccessPlaceholder": "Was",
        "jtbdTriggers": "Auslöser",
        "jtbdTriggersPlaceholder": "Ein Ereignis, das sie von passiv zu aktiv bewegt...",
        "addTrigger": "Auslöser hinzufügen",
        "jtbdAlternatives": "Aktuelle Alternativen",
        "jtbdAlternativesPlaceholder": "Eine Status-quo-Lösung, Wettbewerber oder Umgehung...",
        "addAlternative": "Alternative hinzufügen",
        "objectionsSection": "Einwände",
        "objectionsBlurb": "Hindernisse für Maßnahmen, selbst wenn Schmerz empfunden wird. Jedes weist auf das Konstrukt hin, das es antreibt.",
        "noObjections": "Noch keine Einwände — klicken Sie auf + Einwand hinzufügen oder regenerieren Sie aus KI.",
        "objectionPlaceholder": "Ein Hindernis für Maßnahmen, das diese Persona empfindet...",
        "severity": "Schweregrad (1-10)",
        "addObjection": "Einwand hinzufügen",
        "valuesSection": "Werte",
        "noValues": "Noch keine Werte — klicken Sie auf + Wert hinzufügen oder regenerieren Sie mit KI.",
        "valueChoose": "— wählen Sie einen Wert —",
        "valueRationalePlaceholder": "Warum das für sie wichtig ist...",
        "priority": "Priorität (1-10)",
        "addValue": "Wert hinzufügen",
        "sourcesSection": "Informationsquellen",
        "noSources": "Noch keine Informationsquellen — klicken Sie auf + Quelle hinzufügen oder regenerieren Sie mit KI.",
        "sourcePlaceholder": "z.B. Branchen-Podcasts, LinkedIn-Meinungsführer…",
        "sourceCategory": "Kategorie",
        "sourceCategoryNone": "— Kategorie —",
        "trustLevel": "Vertrauensniveau (1-10)",
        "addSource": "Quelle hinzufügen",
        "quotesSection": "Stimmenproben",
        "quotesBlurb": "Äußerungen in der Ich-Form, jeweils verankert in einem Konstrukt oder Ziel.",
        "noQuotes": "Noch keine Stimmenproben — klicken Sie auf + Zitat hinzufügen oder regenerieren Sie mit KI.",
        "quotePlaceholder": "Ein Satz in der Stimme dieser Persona...",
        "addQuote": "Zitat hinzufügen",
        "frameworkTab": "Rahmenwerk",
        "contextTab": "Kontext & Stimme",
        "regenerateBlurb": "Lassen Sie die KI neu laufen, um frische Ziele & Schmerzpunkte für diese Persona vorzuschlagen.",
        "noFramework": "Diese Sammlung hat kein definiertes Rahmenwerk.",
        "goalPlaceholder": "Ein spezifisches Ziel, das diese Persona erreichen möchte...",
        "addGoal": "Ziel hinzufügen",
        "noGoals": "Noch keine Ziele — klicken Sie auf + Ziel hinzufügen oder regenerieren Sie mit KI.",
        "painPlaceholder": "Eine spezifische Frustration oder unerfülltes Bedürfnis...",
        "addPain": "Schmerzpunkt hinzufügen",
        "noPains": "Noch keine Schmerzpunkte — klicken Sie auf + Schmerzpunkt hinzufügen oder regenerieren Sie mit KI.",
        "derivingContext": "Kontext, Einwände & Stimme ableiten…",
        "derivingContextBlurb": "Die KI berechnet die Aufgaben, Einwände, Werte, Informationsquellen und Stimmenproben dieser Persona aus ihrem Konstruktprofil. Dies dauert normalerweise 10–20 Sekunden."
      },
      "enums": {
        "journeyStage": {
          "unaware": "Unbewusst",
          "considering": "Überlegungen",
          "evaluating": "Bewertung",
          "deciding": "Entscheidung",
          "using": "Verwendung",
          "advocating": "Befürwortung",
          "lapsing": "Nachlassend"
        },
        "sourceCategory": {
          "digital-social": "Digital · Sozial",
          "digital-publishers": "Digital · Verlage",
          "podcasts-audio": "Podcasts · Audio",
          "peer-network": "Peer-Netzwerk",
          "manufacturer-direct": "Hersteller direkt",
          "industry-analysts": "Branchenanalysten",
          "traditional-print": "Traditioneller Druck",
          "in-person-events": "Persönliche Veranstaltungen",
          "other": "Sonstiges"
        },
        "values": {
          "self-direction": "Selbstbestimmung",
          "achievement": "Leistung",
          "security": "Sicherheit",
          "benevolence": "Wohltätigkeit",
          "universalism": "Universalismus",
          "hedonism": "Hedonismus",
          "conformity": "Konformität",
          "tradition": "Tradition",
          "power": "Macht",
          "stimulation": "Stimulation"
        }
      }
    }
  },
  "id": {
    "personas": {
      "collection": {
        "heuristicTooltip": "Tidak ada jangkar akademis — koleksi ini menggunakan dasar heuristik dari perilaku konsumen yang umum.",
        "heuristic": "Dasar heuristik",
        "goals": "Tujuan",
        "painPoints": "Titik sakit",
        "journeyTitle": "Perjalanan",
        "jtbdTitle": "Pekerjaan",
        "objectionsTitle": "Keberatan",
        "voiceTitle": "Suara",
        "valuesAndSources": "Nilai & sumber",
        "sections": "Bagian",
        "drivenBy": "dipicu oleh",
        "feltThrough": "dirasakan melalui",
        "journeyStage": "Tahap perjalanan",
        "antiPersona": "Bukan persona ini",
        "triggers": "Pemicu",
        "alternatives": "Alternatif saat ini",
        "successCriteria": "Keberhasilan:",
        "objectionsSub": "hambatan untuk bertindak",
        "rootedIn": "berakar pada",
        "demonstrates": "menunjukkan",
        "valuesTitle": "Nilai",
        "priority": "Prioritas",
        "sourcesTitle": "Sumber informasi",
        "trust": "Kepercayaan"
      },
      "detailModal": {
        "showLess": "Tampilkan lebih sedikit",
        "showMore": "Tampilkan detail"
      },
      "attributeEditor": {
        "frameworkBlurb": "Nilai seberapa kuat persona ini mewujudkan setiap konstruksi (1-10). Tambahkan interpretasi satu kalimat.",
        "fallbackBlurb": "Dasar heuristik — nilai setiap dimensi 1-10.",
        "importance": "Pentingnya (1-10)",
        "linkConstruct": "Tautkan ke konstruksi kerangka",
        "noConstructLink": "— tidak ada tautan —",
        "regenerateDerivedBlurb": "Menghasilkan kembali konteks, keberatan, nilai, sumber, dan suara dari profil konstruksi persona ini.",
        "regenerating": "Menghasilkan…",
        "regenerate": "Hasilkan kembali",
        "journeySection": "Tahap perjalanan",
        "journeyStage": "Di mana mereka berada dalam perjalanan mereka",
        "journeyNone": "— pilih tahap —",
        "journeyRationale": "Mengapa tahap ini",
        "journeyRationalePlaceholder": "Hubungkan tahap ini dengan skor konstruksi tertentu...",
        "antiPersonaSection": "Bukan persona ini",
        "antiPersonaBlurb": "Apa yang mendiskualifikasi arketipe ini. Cermin dari penyaring perekrutan.",
        "antiPersonaPlaceholder": "Seseorang yang mana persona ini tidak cocok (bukan hanya berbeda — tidak cocok)...",
        "jtbdSection": "Pekerjaan yang harus dilakukan",
        "jtbdJob": "Pekerjaan yang mereka 'sewa' ini untuk",
        "jtbdJobPlaceholder": "mis. Temukan CRM yang dapat berkembang dengan tim saya tanpa membebani saya dengan pengaturan.",
        "jtbdSuccess": "Kriteria keberhasilan",
        "jtbdSuccessPlaceholder": "Apa",
        "jtbdTriggers": "Pemicu",
        "jtbdTriggersPlaceholder": "Sebuah peristiwa yang menggerakkan mereka dari pasif ke aktif...",
        "addTrigger": "Tambahkan pemicu",
        "jtbdAlternatives": "Alternatif saat ini",
        "jtbdAlternativesPlaceholder": "Sebuah solusi status-quo, pesaing, atau jalan keluar...",
        "addAlternative": "Tambahkan alternatif",
        "objectionsSection": "Keberatan",
        "objectionsBlurb": "Hambatan untuk bertindak meskipun rasa sakit dirasakan. Masing-masing menunjukkan konstruksi yang memicunya.",
        "noObjections": "Belum ada keberatan — klik + Tambahkan keberatan, atau hasilkan kembali dari AI.",
        "objectionPlaceholder": "Sebuah hambatan untuk bertindak yang dirasakan oleh persona ini...",
        "severity": "Tingkat keparahan (1-10)",
        "addObjection": "Tambahkan keberatan",
        "valuesSection": "Nilai",
        "noValues": "Belum ada nilai — klik + Tambah nilai, atau regenerasi dari AI.",
        "valueChoose": "— pilih nilai —",
        "valueRationalePlaceholder": "Mengapa ini penting bagi mereka...",
        "priority": "Prioritas (1-10)",
        "addValue": "Tambah nilai",
        "sourcesSection": "Sumber informasi",
        "noSources": "Belum ada sumber informasi — klik + Tambah sumber, atau regenerasi dari AI.",
        "sourcePlaceholder": "mis. podcast industri, pemimpin pemikiran LinkedIn…",
        "sourceCategory": "Kategori",
        "sourceCategoryNone": "— kategori —",
        "trustLevel": "Tingkat kepercayaan (1-10)",
        "addSource": "Tambah sumber",
        "quotesSection": "Contoh suara",
        "quotesBlurb": "Pernyataan orang pertama, masing-masing terikat pada konstruk atau tujuan.",
        "noQuotes": "Belum ada contoh suara — klik + Tambah kutipan, atau regenerasi dari AI.",
        "quotePlaceholder": "Sebuah kalimat orang pertama dalam suara persona ini...",
        "addQuote": "Tambah kutipan",
        "frameworkTab": "Kerangka",
        "contextTab": "Konteks & Suara",
        "regenerateBlurb": "Jalankan kembali AI untuk menyarankan tujuan & titik sakit baru untuk persona ini.",
        "noFramework": "Koleksi ini tidak memiliki kerangka yang ditentukan.",
        "goalPlaceholder": "Sebuah tujuan spesifik yang ingin dicapai oleh persona ini...",
        "addGoal": "Tambah tujuan",
        "noGoals": "Belum ada tujuan — klik + Tambah tujuan, atau regenerasi dari AI.",
        "painPlaceholder": "Sebuah frustrasi spesifik atau kebutuhan yang tidak terpenuhi...",
        "addPain": "Tambah titik sakit",
        "noPains": "Belum ada titik sakit — klik + Tambah titik sakit, atau regenerasi dari AI.",
        "derivingContext": "Menghasilkan konteks, keberatan & suara…",
        "derivingContextBlurb": "AI sedang menghitung pekerjaan yang harus dilakukan persona ini, keberatan, nilai, sumber informasi, dan contoh suara dari profil konstruk mereka. Ini biasanya memakan waktu 10–20 detik."
      },
      "enums": {
        "journeyStage": {
          "unaware": "Tidak sadar",
          "considering": "Mempertimbangkan",
          "evaluating": "Mengevaluasi",
          "deciding": "Memutuskan",
          "using": "Menggunakan",
          "advocating": "Mendukung",
          "lapsing": "Menghilang"
        },
        "sourceCategory": {
          "digital-social": "Digital · Sosial",
          "digital-publishers": "Digital · Penerbit",
          "podcasts-audio": "Podcast · Audio",
          "peer-network": "Jaringan sejawat",
          "manufacturer-direct": "Produsen langsung",
          "industry-analysts": "Analis industri",
          "traditional-print": "Cetak tradisional",
          "in-person-events": "Acara tatap muka",
          "other": "Lainnya"
        },
        "values": {
          "self-direction": "Pengarahan diri",
          "achievement": "Pencapaian",
          "security": "Keamanan",
          "benevolence": "Kebaikan",
          "universalism": "Universalism",
          "hedonism": "Hedonisme",
          "conformity": "Konformitas",
          "tradition": "Tradisi",
          "power": "Kekuasaan",
          "stimulation": "Stimulasi"
        }
      }
    }
  }
};

export default personaV2Labels;
